import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { addDays, isBefore } from 'date-fns';
import {
  AlertCircleIcon,
  ChartColumnIcon,
  ChevronsUpDown,
  DownloadIcon,
  ExternalLinkIcon,
  FilePlusIcon,
  HandCoinsIcon,
  Maximize2Icon,
  Minimize2Icon,
  ScrollTextIcon,
  Table2Icon,
  TicketIcon,
  UserRoundPlusIcon,
  Users2Icon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, XAxis } from 'recharts';
import { useSnapshot } from 'valtio';
import * as XLSX from 'xlsx';

import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../../services/state/auth-state.jsx';
import { getFilteredAnalytics } from '../../../../helpers/filter-campaign-analytics.js';
import { useQueryActiveCampaign } from '../../../../hooks/queries/use-active-campaign.jsx';
import { cn } from '../../../../utils/cn.js';
import {
  CAMPAIGN_COLORS,
  CAMPAIGN_STATES,
} from '../../../../utils/constants.js';
import { timeDifference } from '../../../../utils/dates.js';
import {
  formatToPortugalCurrency,
  formatToPortugalNumeric,
} from '../../../../utils/format.js';
import { greeting } from '../../../../utils/misc.js';

import Loading from '../../../feedback/loading.jsx';
import { DataTableColumnHeader } from '../../../table/data-table-column-header.jsx';
import { Button } from '../../../ui/button.jsx';
import { Calendar } from '../../../ui/calendar-new.jsx';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card.jsx';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '../../../ui/chart.jsx';
import { Combobox } from '../../../ui/combobox.jsx';
import { Label } from '../../../ui/label.jsx';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectList,
  MultiSelectTrigger,
  MultiSelectValue,
  renderMultiSelectOptions,
} from '../../../ui/multi-select.jsx';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../ui/popover.jsx';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../ui/table.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui/tabs.jsx';

const multiselectAnalyticsEnvironmentOptions = [
  { label: 'Online', value: 'online' },
  { label: 'Offline', value: 'offline' },
];

const TABLE_COLUMNS = [
  {
    title: 'Data',
    accessorKey: 'date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data" />
    ),
    cell: ({ row }) => <div>{row.getValue('date')}</div>,
    footer: () => 'Total',
    enableSorting: false,
  },
  {
    title: 'C. Novos Registos',
    accessorKey: 'countCustomers',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="C. Novos Registos" />
    ),
    cell: ({ row }) => <div>{row.getValue('countCustomers')}</div>,
    footer: ({ table }) => {
      const sum = table
        .getFilteredRowModel()
        .rows.reduce((total, row) => total + row.getValue('countCustomers'), 0);

      if (isNaN(sum)) {
        return '---';
      }

      return sum;
    },
    enableSorting: false,
  },
  {
    title: 'C. Novos Participantes',
    accessorKey: 'countNewCustomersParticipating',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="C. Novos Participantes" />
    ),
    cell: ({ row }) => (
      <div>
        {isNaN(row.getValue('countNewCustomersParticipating'))
          ? '---'
          : row.getValue('countNewCustomersParticipating')}
      </div>
    ),
    footer: ({ table }) => {
      const sum = table
        .getFilteredRowModel()
        .rows.reduce(
          (total, row) =>
            total + row.getValue('countNewCustomersParticipating'),
          0,
        );

      if (isNaN(sum)) {
        return '---';
      }

      return sum;
    },
    enableSorting: false,
  },
  {
    title: 'C. Participantes Antigos',
    accessorKey: 'countReturningCustomers',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="C. Participantes Antigos" />
    ),
    cell: ({ row }) => {
      return (
        <div>
          {isNaN(row.getValue('countReturningCustomers'))
            ? '---'
            : row.getValue('countReturningCustomers')}
        </div>
      );
    },
    footer: ({ table }) => {
      const sum = table
        .getFilteredRowModel()
        .rows.reduce(
          (total, row) => total + row.getValue('countReturningCustomers'),
          0,
        );

      if (isNaN(sum)) {
        return '---';
      }

      return sum;
    },
    enableSorting: false,
  },
  {
    title: 'C. Total Participantes',
    accessorKey: 'totalCustomersParticipating',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="C. Total Participantes" />
    ),
    cell: ({ row }) => {
      return (
        <div>
          {isNaN(row.getValue('totalCustomersParticipating'))
            ? '---'
            : row.getValue('totalCustomersParticipating')}
        </div>
      );
    },
    footer: ({ table }) => {
      const sum = table
        .getFilteredRowModel()
        .rows.reduce(
          (total, row) => total + row.getValue('totalCustomersParticipating'),
          0,
        );

      if (isNaN(sum)) {
        return '---';
      }

      return sum;
    },
    enableSorting: false,
  },
  {
    title: 'Faturas Rejeitadas',
    accessorKey: 'countInvoicesRejected',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Faturas Rejeitadas" />
    ),
    cell: ({ row }) => (
      <div>
        {formatToPortugalNumeric.format(row.getValue('countInvoicesRejected'))}
      </div>
    ),
    footer: ({ table }) =>
      formatToPortugalNumeric.format(
        table
          .getFilteredRowModel()
          .rows.reduce(
            (total, row) => total + row.getValue('countInvoicesRejected'),
            0,
          ),
      ),
    enableSorting: false,
  },
  {
    title: 'Faturas',
    accessorKey: 'countInvoices',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Faturas" />
    ),
    cell: ({ row }) => (
      <div>{formatToPortugalNumeric.format(row.getValue('countInvoices'))}</div>
    ),
    footer: ({ table }) =>
      formatToPortugalNumeric.format(
        table
          .getFilteredRowModel()
          .rows.reduce(
            (total, row) => total + row.getValue('countInvoices'),
            0,
          ),
      ),
    enableSorting: false,
  },
  {
    title: 'Total Cupões',
    accessorKey: 'countCoupons',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Cupões" />
    ),
    cell: ({ row }) => (
      <div>{formatToPortugalNumeric.format(row.getValue('countCoupons'))}</div>
    ),
    footer: ({ table }) =>
      formatToPortugalNumeric.format(
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + row.getValue('countCoupons'), 0),
      ),
    enableSorting: false,
  },
  {
    title: 'Total Valor Cupões',
    accessorKey: 'valCoupons',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Valor Cupões (€)" />
    ),
    cell: ({ row }) => (
      <div>{formatToPortugalCurrency.format(row.getValue('valCoupons'))}</div>
    ),
    footer: ({ table }) =>
      formatToPortugalCurrency.format(
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + row.getValue('valCoupons'), 0),
      ),
    enableSorting: false,
  },
  {
    title: 'Total Valor Real',
    accessorKey: 'sumInvoices',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Valor Real (€)" />
    ),
    cell: ({ row }) => (
      <div>{formatToPortugalCurrency.format(row.getValue('sumInvoices'))}</div>
    ),
    footer: ({ table }) =>
      formatToPortugalCurrency.format(
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + row.getValue('sumInvoices'), 0),
      ),
    enableSorting: false,
  },
];

export default function DashboardAdmin() {
  const { user } = useSnapshot(authState);

  const [filters, setFilters] = useState({
    campaign: null,
    date: null,
    environment: multiselectAnalyticsEnvironmentOptions.map(
      (item) => item.value,
    ),
  });

  const [campaignEndDate, setCampaignEndDate] = useState(null);

  const [campaignAnalytics, setCampaignAnalytics] = useState({
    daily: [],
    realtime: {
      cards: {
        countCustomers: 0,
        countInvoices: 0,
        sumInvoices: 0,
        countCoupons: 0,
      },
      graph: {
        coupons: [],
        customers: [],
        invoices: [],
        invoicesRejected: [],
      },
    },
  });

  const [
    campaignAnalyticsMaximizedCharts,
    setCampaignAnalyticsMaximizedCharts,
  ] = useState({});

  const queryActiveCampaign = useQueryActiveCampaign();

  const queryCheckCampaignsToDrawWinners = useQuery({
    queryKey: ['dashboard_campaigns-to-draw-winners'],
    queryFn: async () => {
      const { data: campaignsToDrawWinners } = await msCatalystApi.get(
        `/campaigns?state=${CAMPAIGN_STATES.READY_TO_DRAW}`,
      );

      return campaignsToDrawWinners.data?.[0] || [];
    },
    enabled: queryActiveCampaign.data !== null,
  });

  const queryCampaigns = useQuery({
    queryKey: ['all_campaigns'],
    queryFn: async () => {
      const orderCampaignsByState = (campaigns = []) => {
        const stateOrder = [
          CAMPAIGN_STATES.ONGOING,
          CAMPAIGN_STATES.READY_TO_DRAW,
          CAMPAIGN_STATES.DRAW_CONCLUDED,
          CAMPAIGN_STATES.CONCLUDED,
        ];

        campaigns.sort((campaign1, campaign2) => {
          const state1 = campaign1.state;
          const state2 = campaign2.state;

          const index1 = stateOrder.indexOf(state1);
          const index2 = stateOrder.indexOf(state2);

          return index1 - index2;
        });

        return campaigns;
      };

      const { data: campaignsRes } = await msCatalystApi.get(
        `/campaigns?state=in:${CAMPAIGN_STATES.ONGOING},${CAMPAIGN_STATES.POST_END_DATE},${CAMPAIGN_STATES.READY_TO_DRAW},${CAMPAIGN_STATES.DRAW_CONCLUDED},${CAMPAIGN_STATES.CONCLUDED}`,
      );
      const data = orderCampaignsByState(campaignsRes.data);
      const selectedCampaign = data?.[0];

      const closeDate = addDays(new Date(selectedCampaign.closeDate), 1);
      closeDate.setHours(0, 0, 0, 0);

      let limitDate = addDays(new Date(selectedCampaign.closeDate), 1);
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);

      if (isBefore(currentDate, closeDate)) {
        limitDate = currentDate;
      }

      setCampaignEndDate(limitDate);

      setFilters((prev) => ({
        ...prev,
        campaign: selectedCampaign,
        date: {
          from: new Date(selectedCampaign.startDate),
          to: limitDate,
        },
      }));

      return data;
    },
  });

  const queryAnalytics = useQuery({
    queryKey: ['dashboard_analytics'],
    queryFn: async () => {
      const { data: analytics } =
        await msCatalystApi.post(`/dashboard/analytics`);

      return analytics.data;
    },
  });

  const queryCampaignAnalytics = useQuery({
    queryKey: ['dashboard_campaign_analytics', filters],
    queryFn: async () => {
      // Fire off both requests in parallel
      const [graphPromise, dailyPromise] = [
        msCatalystApi.post('/dashboard/analytics-campaign', {
          campaign: filters.campaign,
        }),
        msCatalystApi.post('/dashboard/analytics-campaign-daily', {
          campaign: filters.campaign,
        }),
      ];

      // Wait for both promises to resolve
      const [graphResponse, dailyResponse] = await Promise.all([
        graphPromise,
        dailyPromise,
      ]);

      // Return a single, structured object
      return {
        realtime: graphResponse.data.data,
        daily: dailyResponse.data.data,
      };
    },
    enabled: !!queryCampaigns?.isSuccess && !!filters?.campaign?.ROWID,
  });

  useEffect(() => {
    if (queryCampaignAnalytics.isSuccess && queryCampaignAnalytics.data) {
      setCampaignAnalytics(
        getFilteredAnalytics(queryCampaignAnalytics.data, filters),
      );
    }
  }, [queryCampaignAnalytics.isSuccess, queryCampaignAnalytics.data, filters]);

  const defaultData = useMemo(() => [], []);

  const tableAnalyticsCampaignDaily = useReactTable({
    data: campaignAnalytics?.daily || defaultData,
    columns: TABLE_COLUMNS,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
    manualPagination: false,
  });

  const analyticsTimeToUpdate = timeDifference(
    queryAnalytics?.data?.lastUpdate,
    2,
  );

  const cards = {
    global: [
      {
        title: 'Total Clientes',
        icon: <Users2Icon className="size-4 text-muted-foreground" />,
        value: formatToPortugalNumeric.format(
          queryAnalytics?.data?.cards?.countCustomers || 0,
        ),
      },
      {
        title: 'Total Campanhas',
        icon: <HandCoinsIcon className="size-4 text-muted-foreground" />,
        value: queryAnalytics?.data?.cards?.countCampaigns || 0,
      },
      {
        title: 'Valor Faturas',
        icon: <ScrollTextIcon className="size-4 text-muted-foreground" />,
        value: formatToPortugalCurrency.format(
          queryAnalytics?.data?.cards?.sumInvoices || 0,
        ),
      },
    ],
    campaign: [
      {
        title: 'Total Clientes',
        icon: <Users2Icon className="size-4 text-muted-foreground" />,
        value: formatToPortugalNumeric.format(
          campaignAnalytics?.realtime.cards?.countCustomers || 0,
        ),
      },
      {
        title: 'Total Faturas',
        icon: <ScrollTextIcon className="size-4 text-muted-foreground" />,
        value: formatToPortugalNumeric.format(
          campaignAnalytics?.realtime.cards?.countInvoices || 0,
        ),
      },
      {
        title: 'Valor Faturas',
        icon: <ScrollTextIcon className="size-4 text-muted-foreground" />,
        value: formatToPortugalCurrency.format(
          campaignAnalytics?.realtime.cards?.sumInvoices || 0,
        ),
      },
      {
        title: 'Total Cupões',
        icon: <TicketIcon className="size-4 text-muted-foreground" />,
        value: formatToPortugalNumeric.format(
          campaignAnalytics?.realtime.cards?.countCoupons || 0,
        ),
      },
    ],
  };

  const campaignAnalyticsCharts = [
    {
      key: 'invoicesCount',
      title: 'Total Faturas',
      data: campaignAnalytics?.realtime?.graph?.invoices,
      config: {
        name: {
          label: 'Dia/Hora',
          color: '#2563eb',
        },
        count: {
          label: 'Nº de Faturas',
          color: '#ef4444',
        },
      },
      xDataKey: 'name',
      chartDataKey: 'count',
      chartColour: '#ef4444',
      isCurrency: false,
    },
    {
      key: 'invoicesSum',
      title: 'Faturas Valor (€)',
      data: campaignAnalytics?.realtime?.graph?.invoices,
      config: {
        name: {
          label: 'Dia/Hora',
          color: '#2563eb',
        },
        total: {
          label: 'Valor de Faturas (€)',
          color: '#ef4444',
        },
      },
      xDataKey: 'name',
      chartDataKey: 'total',
      chartColour: '#ef4444',
      isCurrency: true,
    },
    {
      key: 'invoicesRejectedCount',
      title: 'Total Faturas Rejeitadas',
      data: campaignAnalytics?.realtime?.graph?.invoicesRejected,
      config: {
        name: {
          label: 'Dia/Hora',
          color: '#2563eb',
        },
        count: {
          label: 'Nº Faturas Rejeitadas',
          color: '#ef4444',
        },
      },
      xDataKey: 'name',
      chartDataKey: 'count',
      chartColour: '#ef4444',
      isCurrency: false,
    },
    {
      key: 'invoicesRejectedSum',
      title: 'Faturas Rejeitadas Valor (€)',
      data: campaignAnalytics?.realtime?.graph?.invoicesRejected,
      config: {
        name: {
          label: 'Dia/Hora',
          color: '#2563eb',
        },
        total: {
          label: 'Valor Faturas Rejeitadas (€)',
          color: '#ef4444',
        },
      },
      xDataKey: 'name',
      chartDataKey: 'total',
      chartColour: '#ef4444',
      isCurrency: true,
    },
    {
      key: 'couponsCount',
      title: 'Total Cupões',
      data: campaignAnalytics?.realtime?.graph?.coupons,
      config: {
        name: {
          label: 'Dia/Hora',
          color: '#2563eb',
        },
        value: {
          label: 'Nº de Cupões',
          color: '#ef4444',
        },
      },
      xDataKey: 'name',
      chartDataKey: 'value',
      chartColour: '#ef4444',
      isCurrency: false,
    },
    {
      key: 'customersCount',
      title: 'Total Clientes',
      data: campaignAnalytics?.realtime?.graph?.customers,
      config: {
        name: {
          label: 'Dia/Hora',
          color: '#2563eb',
        },
        value: {
          label: 'Nº de Clientes',
          color: '#ef4444',
        },
      },
      xDataKey: 'name',
      chartDataKey: 'value',
      chartColour: '#ef4444',
      isCurrency: false,
    },
  ];

  const exportAnalyticsData = async () => {
    const timestamp = new Date()?.getTime();

    const addTotalsRow = (data) => {
      if (!data.length) return data;
      const totals = [];

      // First column label
      totals[0] = 'Total';

      // For each remaining column, try summing numeric values
      for (let col = 1; col < data[0].length; col++) {
        let sum = 0;
        let isNumeric = true;

        for (let row = 1; row < data.length; row++) {
          const val = data[row][col];
          const num = Number(val);
          if (!isNaN(num)) {
            sum += num;
          } else {
            isNumeric = false;
            break;
          }
        }

        totals[col] = isNumeric ? sum : '';
      }

      return [...data, totals];
    };

    // queryCampaignAnalytics
    const totalAnalytics = getFilteredAnalytics(queryCampaignAnalytics.data, {
      environment: ['online', 'offline'],
      campaign: filters.campaign,
      date: {
        from: new Date(filters.campaign.startDate),
        to: new Date(filters.campaign.closeDate),
      },
    });
    console.log('totalAnalytics', totalAnalytics);
    const onlineAnalytics = getFilteredAnalytics(queryCampaignAnalytics.data, {
      environment: ['online'],
      campaign: filters.campaign,
      date: {
        from: new Date(filters.campaign.startDate),
        to: new Date(filters.campaign.closeDate),
      },
    });
    const offlineAnalytics = getFilteredAnalytics(queryCampaignAnalytics.data, {
      environment: ['offline'],
      campaign: filters.campaign,
      date: {
        from: new Date(filters.campaign.startDate),
        to: new Date(filters.campaign.closeDate),
      },
    });

    const analyticsData = [
      { env: 'total', data: totalAnalytics },
      { env: 'online', data: onlineAnalytics },
      { env: 'offline', data: offlineAnalytics },
    ];

    for (let index = 0; index < analyticsData.length; index++) {
      const workbook = XLSX.utils.book_new();

      const analyticsItem = analyticsData[index];

      const dailyHeaders = TABLE_COLUMNS.map((col) => col.title);

      const dailyData = analyticsItem.data.daily.map((item) => [
        item.date,
        item.countCustomers,
        item.countNewCustomersParticipating,
        item.countReturningCustomers,
        item.totalCustomersParticipating,
        item.countInvoicesRejected,
        item.countInvoices,
        item.countCoupons,
        item.sumInvoices,
        // item.sumInvoicesRejected
        item.valCoupons,
      ]);

      console.log('dailyData', dailyData);

      const dailySheetData = addTotalsRow([dailyHeaders, ...dailyData]);
      const dailySheet = XLSX.utils.aoa_to_sheet(dailySheetData);

      XLSX.utils.book_append_sheet(
        workbook,
        dailySheet,
        'Estatísticas Diárias',
      );

      const campaignAnalyticsExportCharts = [
        {
          key: 'invoicesCount',
          title: 'Total Faturas',
          data: analyticsItem.data?.realtime?.graph?.invoices,
          config: {
            name: {
              label: 'Dia/Hora',
              color: '#2563eb',
            },
            count: {
              label: 'Nº de Faturas',
              color: '#ef4444',
            },
          },
          xDataKey: 'name',
          chartDataKey: 'count',
          chartColour: '#ef4444',
          isCurrency: false,
        },
        {
          key: 'invoicesSum',
          title: 'Faturas Valor (€)',
          data: analyticsItem.data?.realtime?.graph?.invoices,
          config: {
            name: {
              label: 'Dia/Hora',
              color: '#2563eb',
            },
            total: {
              label: 'Valor de Faturas (€)',
              color: '#ef4444',
            },
          },
          xDataKey: 'name',
          chartDataKey: 'total',
          chartColour: '#ef4444',
          isCurrency: true,
        },
        {
          key: 'invoicesRejectedCount',
          title: 'Total Faturas Rejeitadas',
          data: analyticsItem.data?.realtime?.graph?.invoicesRejected,
          config: {
            name: {
              label: 'Dia/Hora',
              color: '#2563eb',
            },
            count: {
              label: 'Nº Faturas Rejeitadas',
              color: '#ef4444',
            },
          },
          xDataKey: 'name',
          chartDataKey: 'count',
          chartColour: '#ef4444',
          isCurrency: false,
        },
        {
          key: 'invoicesRejectedSum',
          title: 'Faturas Rejeitadas Valor (€)',
          data: analyticsItem.data?.realtime?.graph?.invoicesRejected,
          config: {
            name: {
              label: 'Dia/Hora',
              color: '#2563eb',
            },
            total: {
              label: 'Valor Faturas Rejeitadas (€)',
              color: '#ef4444',
            },
          },
          xDataKey: 'name',
          chartDataKey: 'total',
          chartColour: '#ef4444',
          isCurrency: true,
        },
        {
          key: 'couponsCount',
          title: 'Total Cupões',
          data: analyticsItem.data?.realtime?.graph?.coupons,
          config: {
            name: {
              label: 'Dia/Hora',
              color: '#2563eb',
            },
            value: {
              label: 'Nº de Cupões',
              color: '#ef4444',
            },
          },
          xDataKey: 'name',
          chartDataKey: 'value',
          chartColour: '#ef4444',
          isCurrency: false,
        },
        {
          key: 'customersCount',
          title: 'Total Clientes',
          data: analyticsItem.data?.realtime?.graph?.customers,
          config: {
            name: {
              label: 'Dia/Hora',
              color: '#2563eb',
            },
            value: {
              label: 'Nº de Clientes',
              color: '#ef4444',
            },
          },
          xDataKey: 'name',
          chartDataKey: 'value',
          chartColour: '#ef4444',
          isCurrency: false,
        },
      ];

      // 2. Charts data
      campaignAnalyticsExportCharts.forEach((chart) => {
        if (!chart.data?.length) return;

        // Extract the headers from the config object
        const xKey = chart.xDataKey;
        const yKey = chart.chartDataKey;

        // First column will always be the xDataKey label
        const headers = [
          chart.config.name.label,
          chart.config[yKey]?.label || yKey,
        ];

        // Map chart data
        const chartData = chart.data.map((item) => [item[xKey], item[yKey]]);

        console.log('chartData', chartData);

        const chartSheetData = addTotalsRow([headers, ...chartData]);
        const sheet = XLSX.utils.aoa_to_sheet(chartSheetData);

        XLSX.utils.book_append_sheet(workbook, sheet, chart.title);
      });

      // 3. Save the workbook
      XLSX.writeFile(
        workbook,
        `${timestamp}_analytics_${analyticsItem.env}_${filters?.campaign?.name}.xlsx`,
      );
    }

    return;

    /*
    // 1. Daily data
    const dailyRows = tableAnalyticsCampaignDaily.getRowModel().rows;

    if (!dailyRows.length) {
      toast.error('Não existem dados para exportar.');
      return;
    }

    const dailyHeaders = TABLE_COLUMNS.map((col) => col.title);
    const dailyData = dailyRows.map((row) =>
      row.getVisibleCells().map((cell) => cell.getValue()),
    );

    const dailySheetData = addTotalsRow([dailyHeaders, ...dailyData]);
    const dailySheet = XLSX.utils.aoa_to_sheet(dailySheetData);

    XLSX.utils.book_append_sheet(workbook, dailySheet, 'Estatísticas Diárias');

    // 2. Charts data
    campaignAnalyticsCharts.forEach((chart) => {
      if (!chart.data?.length) return;

      // Extract the headers from the config object
      const xKey = chart.xDataKey;
      const yKey = chart.chartDataKey;

      // First column will always be the xDataKey label
      const headers = [
        chart.config.name.label,
        chart.config[yKey]?.label || yKey,
      ];

      // Map chart data
      const chartData = chart.data.map((item) => [item[xKey], item[yKey]]);

      const chartSheetData = addTotalsRow([headers, ...chartData]);
      const sheet = XLSX.utils.aoa_to_sheet(chartSheetData);

      XLSX.utils.book_append_sheet(workbook, sheet, chart.title);
    });

    // 3. Save the workbook
    XLSX.writeFile(
      workbook,
      `${timestamp}_analytics_${filters?.campaign?.name}.xlsx`,
    );
    */
  };

  return (
    <div className="flex flex-col space-y-4">
      {queryCheckCampaignsToDrawWinners?.data?.length ? (
        <>
          <div>
            <h2 className="flex flex-row items-center gap-2 pb-3 text-3xl font-semibold text-orange-600">
              <AlertCircleIcon className="size-8" />
              Avisos
            </h2>
          </div>
          <Card className="mb-10 border-orange-600 bg-orange-500/20 p-6">
            <h2 className="flex flex-row items-center gap-2 pb-3 text-xl font-semibold">
              <span>
                A campanha {queryCheckCampaignsToDrawWinners?.data?.name}{' '}
                terminou.
              </span>
            </h2>
            <Button asChild variant="outline">
              <Link
                className="flex h-auto flex-wrap gap-2 text-balance md:text-base lg:text-base"
                to={`/backoffice/campaigns/${queryCheckCampaignsToDrawWinners?.data?.ROWID}`}
              >
                <ExternalLinkIcon className="size-4 md:size-5" />
                Sortear Vencedor
              </Link>
            </Button>
          </Card>
        </>
      ) : null}

      <div className="flex flex-col items-start space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            {greeting()}, {user?.firstName} {user?.lastName} 👋
          </h2>
          {queryAnalytics.isSuccess &&
            analyticsTimeToUpdate != null &&
            parseInt(analyticsTimeToUpdate) >= 0 && (
              <span className="text-sm text-muted-foreground">
                Esta página vai ser atualizada dentro de {analyticsTimeToUpdate}
              </span>
            )}
        </div>

        {queryActiveCampaign?.data ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button asChild>
              <Link to="/backoffice/customers/create">
                <UserRoundPlusIcon />
                Criar Cliente
              </Link>
            </Button>

            <Button asChild>
              <Link to="/backoffice/invoices/create">
                <FilePlusIcon />
                Inserir Fatura
              </Link>
            </Button>
          </div>
        ) : null}
      </div>

      {/* cards - Informações globais */}
      <div className="!mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {cards.global.map((card, index) => {
          return (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {card.title}
                </CardTitle>
                {card.icon}
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{card.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="py-4">
        {queryCampaigns.isLoading && queryCampaignAnalytics.isLoading ? (
          <Loading />
        ) : (
          <>
            <h1 className="text-lg font-semibold">Estatísticas por Campanha</h1>

            {/* filters - Informações da campanha */}
            <div className="mt-4 flex w-full flex-col items-center justify-between gap-4 lg:w-auto lg:flex-row">
              <div className="flex w-full flex-col gap-4 lg:w-auto lg:flex-row">
                <div className="flex w-full flex-col gap-y-2 lg:w-auto">
                  <Label htmlFor="dates" className="px-1">
                    Campanha
                  </Label>
                  <Combobox
                    isLoading={queryCampaigns.isLoading}
                    isError={queryCampaigns.isError}
                    items={queryCampaigns?.data}
                    value={filters.campaign?.name}
                    valueObject={filters?.campaign}
                    status={{
                      key: 'state',
                      colors: CAMPAIGN_COLORS,
                    }}
                    labelKey="name"
                    onChange={(value) => {
                      setFilters((prev) => ({
                        ...prev,
                        campaign: value,
                        date: {
                          from: new Date(value.startDate),
                          to: new Date(value.closeDate),
                        },
                      }));
                    }}
                  />
                </div>

                <div className="flex w-full flex-col gap-y-2 lg:w-auto">
                  <Label htmlFor="dates" className="px-1">
                    Período
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="dates"
                        variant="outline"
                        className="w-full justify-between font-normal lg:w-56"
                      >
                        {filters?.date?.from && filters?.date?.to
                          ? `${filters?.date?.from.toLocaleDateString()} - ${filters?.date?.to.toLocaleDateString()}`
                          : 'Selecione uma data'}
                        <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto overflow-hidden p-0"
                      align="start"
                    >
                      <Calendar
                        mode="range"
                        defaultMonth={filters?.date?.from}
                        selected={filters?.date}
                        onSelect={(val) =>
                          setFilters((prev) => ({
                            ...prev,
                            date: val,
                          }))
                        }
                        numberOfMonths={2}
                        className="rounded-lg border shadow-sm"
                        disabled={{
                          after: campaignEndDate ?? null,
                          before: filters?.campaign?.startDate,
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex w-full flex-col gap-y-2 lg:w-auto">
                  <Label htmlFor="environment" className="px-1">
                    Ambiente
                  </Label>
                  <MultiSelect
                    value={filters.environment}
                    onValueChange={(values) => {
                      setFilters((prev) => ({
                        ...prev,
                        environment: values,
                      }));
                    }}
                  >
                    <MultiSelectTrigger>
                      <MultiSelectValue />
                    </MultiSelectTrigger>
                    <MultiSelectContent>
                      <MultiSelectList>
                        {renderMultiSelectOptions(
                          multiselectAnalyticsEnvironmentOptions,
                        )}
                      </MultiSelectList>
                    </MultiSelectContent>
                  </MultiSelect>
                </div>
              </div>

              <Button
                className="w-full lg:w-auto"
                onClick={exportAnalyticsData}
              >
                <DownloadIcon />
                Exportar
              </Button>
            </div>

            <div className="relative mt-8 flex flex-col gap-y-8">
              <div className="col-span-1 flex w-full flex-col gap-4 md:col-span-2 xl:col-span-4">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  {cards.campaign.map((card, index) => (
                    <Card key={index}>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                          {card.title}
                        </CardTitle>
                        {card.icon}
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{card.value}</div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <Tabs defaultValue="realtime">
                <TabsList className="mb-3">
                  <TabsTrigger value="realtime">
                    <ChartColumnIcon className="mr-1 size-4" />
                    Estatísticas - Gráficos
                  </TabsTrigger>
                  <TabsTrigger value="daily">
                    <Table2Icon className="mr-1 size-4" /> Estatísticas - Tabela
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="realtime">
                  <div className={cn('grid grid-cols-1 gap-4 lg:grid-cols-2')}>
                    {campaignAnalyticsCharts.map((item, key) => {
                      return (
                        <Card
                          key={key}
                          className={cn(
                            'grid grid-cols-1',
                            campaignAnalyticsMaximizedCharts?.[item.key]
                              ? 'lg:col-span-2'
                              : 'col-span-1',
                          )}
                        >
                          <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle>{item.title}</CardTitle>

                            {campaignAnalyticsMaximizedCharts?.[item.key] ? (
                              <Button
                                className="hidden lg:block"
                                variant="ghost"
                                onClick={() => {
                                  setCampaignAnalyticsMaximizedCharts(
                                    (prev) => {
                                      const { [item.key]: _, ...rest } = prev;
                                      return rest;
                                    },
                                  );
                                }}
                              >
                                <Minimize2Icon />
                              </Button>
                            ) : (
                              <Button
                                className="hidden lg:block"
                                variant="ghost"
                                onClick={() => {
                                  setCampaignAnalyticsMaximizedCharts({
                                    ...campaignAnalyticsMaximizedCharts,
                                    [item.key]: 1,
                                  });
                                }}
                              >
                                <Maximize2Icon />
                              </Button>
                            )}
                          </CardHeader>

                          <CardContent className="pl-2">
                            <ChartContainer
                              config={item.config}
                              className="h-[300px] w-full"
                            >
                              <BarChart accessibilityLayer data={item.data}>
                                <CartesianGrid vertical={false} />
                                <XAxis
                                  dataKey={item.xDataKey}
                                  tickLine={false}
                                  tickMargin={10}
                                  axisLine={false}
                                  tickFormatter={(value) => value}
                                />
                                <ChartTooltip
                                  content={<ChartTooltipContent />}
                                />
                                <ChartLegend content={<ChartLegendContent />} />
                                <Bar
                                  dataKey={item.chartDataKey}
                                  fill={item.chartColour}
                                  radius={2}
                                />
                                <ChartLegend content={<ChartLegendContent />} />
                              </BarChart>
                            </ChartContainer>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </TabsContent>

                <TabsContent value="daily">
                  <div className="rounded-md border border-muted-foreground/35">
                    <Table>
                      <TableHeader>
                        {tableAnalyticsCampaignDaily
                          .getHeaderGroups()
                          .map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                              {headerGroup.headers.map((header) => {
                                return (
                                  <TableHead
                                    key={header.id}
                                    colSpan={header.colSpan}
                                  >
                                    {header.isPlaceholder
                                      ? null
                                      : flexRender(
                                          header.column.columnDef.header,
                                          header.getContext(),
                                        )}
                                  </TableHead>
                                );
                              })}
                            </TableRow>
                          ))}
                      </TableHeader>

                      <TableBody>
                        {tableAnalyticsCampaignDaily.getRowModel().rows
                          ?.length ? (
                          tableAnalyticsCampaignDaily
                            .getRowModel()
                            .rows.map((row) => (
                              <TableRow key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                  <TableCell key={cell.id}>
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext(),
                                    )}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={TABLE_COLUMNS.length}
                              className="h-24 text-center"
                            >
                              {tableAnalyticsCampaignDaily.getState()
                                .columnFilters.length
                                ? 'Não foram encontrados resultados.'
                                : 'Não existem quaisquer estatísticas desta campanha.'}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>

                      <TableFooter className="border-t border-muted-foreground/35 font-bold">
                        {tableAnalyticsCampaignDaily
                          .getFooterGroups()
                          .map((footerGroup) => (
                            <TableRow key={footerGroup.id}>
                              {footerGroup.headers.map((footer) => (
                                <TableCell
                                  key={footer.id}
                                  colSpan={footer.colSpan}
                                >
                                  {footer.isPlaceholder
                                    ? null
                                    : flexRender(
                                        footer.column.columnDef.footer,
                                        footer.getContext(),
                                      )}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                      </TableFooter>
                    </Table>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
